// Shared pin clustering for the references maps: pins that overlap on screen are
// replaced by a numbered cluster pin, and activating a cluster zooms in until its
// members separate. Spread into an Alpine component next to mapZoom() and call
// initPinClustering() at the end of init().
//
// The host component must provide:
//   this.map   svg-pan-zoom instance (from mapZoom)
//   this.zoom  current zoom level (from mapZoom)
//   this.$root an element containing the .imwa__map__image SVG with its
//              .imwa__map__image-groups layer
// and each pin element must carry data-pin-id, data-transform and data-pin-size.
// Pins hide themselves with x-show="!isPinClustered('<id>')".
//
// Known limitation: pins closer than ~1.5 viewBox units (~1.4‰ horizontally,
// ~2.5‰ vertically) cannot be separated at all, because the zoom that would do it
// is past the map's maxZoom (20 desktop / 30 mobile). Those pins are therefore
// never clustered — they stay stacked, so their links remain reachable. Showing
// them in a popover instead is the intended follow-up, which is why a group keeps
// its full pin list rather than just a count.

import { mapMaxZoom } from './map-zoom';

const GROUP_LAYER_CLASS = 'imwa__map__image-groups';
const PIN_CLASS = 'imwa__map__pin';
const GROUP_PIN_CLASS = 'imwa__map__pin--is-group';
const DISABLED_PIN_CLASS = 'imwa__map__pin--is-disabled';
const ANIMATED_CLASS = 'imwa__map--is-animated';

// Cluster radius in viewBox units at zoom 1. Pins are scaled by min(2/zoom, 1),
// so dividing by the zoom keeps the radius roughly constant on screen. The radius
// is deliberately ~0.55 of a pin width rather than a full one: pins are grouped
// once they clearly overlap, not as soon as they touch. Widening it would cluster
// far more aggressively at the default zoom, so it stays as the original map had it.
const CLUSTER_RADIUS = 30;
// Pins are rendered 1.5x larger below this width, so they overlap 1.5x sooner
const MOBILE_BREAKPOINT = 768;
const MOBILE_FACTOR = 1.5;
// Zoom just past the point where the two closest members stop overlapping
const SEPARATION_MARGIN = 1.1;
// Below this zoom the pins stop shrinking with the map (their scale factor is
// clamped at 1), so a lower target would leave the members still overlapping.
const MIN_SEPARATION_ZOOM = 2;
// Guards against a second activation landing inside the 300ms zoom animation
const ACTIVATION_DEBOUNCE_MS = 500;
// How far a finger may travel and still count as a tap rather than a pan
const TAP_SLOP_PX = 10;
const PIN_SIZE = { width: 27.5, height: 36 };

const isMobileViewport = () => window.innerWidth < MOBILE_BREAKPOINT;

// window.neos only exists inside the Neos UI guest frame; a document opened
// directly in a user workspace carries the body class instead.
const isNeosBackend = () => !!window.neos || document.body.classList.contains('neos-backend');

export const pinClustering = () => ({
    groupPins: [],
    clusteredPinIds: [],
    // -Infinity, not 0: event.timeStamp is measured from page load, so 0 would
    // swallow an activation that happens within the debounce window of it
    lastGroupActivation: -Infinity,
    clusterMapImage: null,
    pendingClusterTouch: null,
    settleTimer: null,
    zoomNudgeTimer: null,
    animationTimer: null,

    initPinClustering() {
        // In the Neos backend pins are placed from the inspector, so every pin has
        // to stay individually visible and positionable — no clustering there.
        if (isNeosBackend()) {
            return;
        }
        const mapImage = this.$root.querySelector('.imwa__map__image');
        if (!mapImage) {
            return;
        }

        // Touch needs its own handling: svg-pan-zoom calls preventDefault() on
        // touchstart, which suppresses the compatibility click, so a tap would
        // otherwise never reach the click handler. Activating on touchstart alone
        // (as the original map did) is wrong too — it fires when a finger merely
        // starts panning — so the gesture is only treated as a tap if the finger
        // stayed put until touchend.
        this.clusterMapImage = mapImage;
        this.onClusterClick = (event) => {
            const groupPin = event.target.closest && event.target.closest(`.${GROUP_PIN_CLASS}`);
            if (groupPin) {
                this.activateGroupPin(groupPin, event.timeStamp);
            }
        };
        this.onClusterTouchStart = (event) => {
            const groupPin = event.target.closest && event.target.closest(`.${GROUP_PIN_CLASS}`);
            const touch = event.touches && event.touches.length === 1 ? event.touches[0] : null;
            this.pendingClusterTouch =
                groupPin && touch ? { groupPin, x: touch.clientX, y: touch.clientY } : null;
        };
        this.onClusterTouchEnd = (event) => {
            const pending = this.pendingClusterTouch;
            this.pendingClusterTouch = null;
            const touch = event.changedTouches && event.changedTouches[0];
            if (!pending || !touch) {
                return;
            }
            const travelled = Math.hypot(touch.clientX - pending.x, touch.clientY - pending.y);
            if (travelled <= TAP_SLOP_PX) {
                this.activateGroupPin(pending.groupPin, event.timeStamp);
            }
        };
        this.onClusterKeydown = (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') {
                return;
            }
            const groupPin = event.target.closest && event.target.closest(`.${GROUP_PIN_CLASS}`);
            if (groupPin) {
                event.preventDefault();
                this.activateGroupPin(groupPin, event.timeStamp);
            }
        };
        mapImage.addEventListener('click', this.onClusterClick);
        mapImage.addEventListener('keydown', this.onClusterKeydown);
        mapImage.addEventListener('touchstart', this.onClusterTouchStart);
        mapImage.addEventListener('touchend', this.onClusterTouchEnd);

        this.recalculatePinClusters();
        // svg-pan-zoom applies its initial fit/center asynchronously; recompute
        // once it settled so the first clusters match what is on screen
        this.settleTimer = setTimeout(() => this.recalculatePinClusters(), 300);
    },

    // Hosts call this from their Alpine destroy() so a re-initialised tree does
    // not end up with the listeners bound twice.
    destroyPinClustering() {
        if (this.clusterMapImage) {
            this.clusterMapImage.removeEventListener('click', this.onClusterClick);
            this.clusterMapImage.removeEventListener('keydown', this.onClusterKeydown);
            this.clusterMapImage.removeEventListener('touchstart', this.onClusterTouchStart);
            this.clusterMapImage.removeEventListener('touchend', this.onClusterTouchEnd);
            this.clusterMapImage = null;
        }
        clearTimeout(this.settleTimer);
        clearTimeout(this.zoomNudgeTimer);
        clearTimeout(this.animationTimer);
    },

    activateGroupPin(groupPinElement, timeStamp) {
        if (timeStamp - this.lastGroupActivation < ACTIVATION_DEBOUNCE_MS) {
            return;
        }
        this.lastGroupActivation = timeStamp;
        this.zoomIntoGroup(groupPinElement);
    },

    // Called by mapZoom's applyZoom so clusters follow every zoom step
    onMapZoomChanged() {
        this.recalculatePinClusters();
    },

    recalculatePinClusters() {
        if (isNeosBackend() || !this.map) {
            return;
        }
        this.calculateGroupPins();
        this.renderGroupPins();
    },

    calculateGroupPins() {
        const pins = Array.from(
            this.$root.querySelectorAll(`.${PIN_CLASS}:not(.${GROUP_PIN_CLASS})`)
        );
        const pinPositions = pins
            .map((pin) => {
                const match = (pin.dataset.transform || '').match(/translate\(([^,]+),([^)]+)\)/);
                if (!match) {
                    return null;
                }
                return {
                    x: match[1] * 1,
                    y: match[2] * 1,
                    pinId: pin.dataset.pinId,
                    element: pin,
                    isPartOfGroup: false,
                };
            })
            .filter(Boolean);

        const mobileFactor = isMobileViewport() ? MOBILE_FACTOR : 1;
        const threshold = (CLUSTER_RADIUS / this.zoom) * mobileFactor;

        // Pins this close can never be pulled apart — the zoom that would do it is
        // past the map's maxZoom. Clustering them would hide links that nothing can
        // bring back (on the ReferencesMap the pin is the only route to the page),
        // so they are left stacked, exactly as the map behaved before clustering.
        // Reference pages whose position was never set all share the 500/500
        // default, so this is the normal case, not a pathological one.
        const minSeparableDistance = (CLUSTER_RADIUS * mobileFactor) / mapMaxZoom();
        const clusterablePins = pinPositions.filter((pin) =>
            pinPositions.every(
                (other) => other === pin || this.distanceBetween(pin, other) > minSeparableDistance
            )
        );

        const groupPins = [];

        clusterablePins.forEach((pinPosition, index) => {
            if (pinPosition.isPartOfGroup) {
                return;
            }

            // is the pin near an existing group?
            groupPins.forEach((groupPin) => {
                if (pinPosition.isPartOfGroup) {
                    return;
                }
                if (this.distanceBetween(pinPosition, groupPin) <= threshold) {
                    pinPosition.isPartOfGroup = true;
                    groupPin.pins.push(pinPosition);
                }
            });

            if (pinPosition.isPartOfGroup) {
                return;
            }

            // is the pin near another ungrouped pin?
            clusterablePins.forEach((otherPinPosition, otherIndex) => {
                if (pinPosition.isPartOfGroup || otherPinPosition.isPartOfGroup || index === otherIndex) {
                    return;
                }
                if (this.distanceBetween(pinPosition, otherPinPosition) <= threshold) {
                    pinPosition.isPartOfGroup = true;
                    otherPinPosition.isPartOfGroup = true;
                    groupPins.push({
                        x: (pinPosition.x + otherPinPosition.x) / 2,
                        y: (pinPosition.y + otherPinPosition.y) / 2,
                        pins: [pinPosition, otherPinPosition],
                    });
                }
            });
        });

        // the centre moves as pins join, so average once the groups are complete
        groupPins.forEach((groupPin) => {
            groupPin.x = groupPin.pins.reduce((acc, pin) => acc + pin.x, 0) / groupPin.pins.length;
            groupPin.y = groupPin.pins.reduce((acc, pin) => acc + pin.y, 0) / groupPin.pins.length;
        });

        this.groupPins = groupPins;
        // reassigned wholesale so Alpine picks the change up in every pin's x-show
        this.clusteredPinIds = groupPins.reduce(
            (ids, groupPin) => ids.concat(groupPin.pins.map((pin) => pin.pinId)),
            []
        );
    },

    distanceBetween(a, b) {
        const dx = a.x - b.x;
        const dy = a.y - b.y;
        return Math.sqrt(dx * dx + dy * dy);
    },

    isPinClustered(pinId) {
        return this.clusteredPinIds.includes(pinId);
    },

    // A cluster is disabled when none of its pins is selectable. Components with
    // client-side filters decide that with isPinSelectable(); where the state is
    // server-rendered the pin element already carries the disabled class. The
    // codes are parsed defensively because a pin without countryCodes renders
    // data-country-codes="null".
    isClusterPinSelectable(pinElement) {
        const countryCodes = JSON.parse(pinElement.dataset.countryCodes || 'null');
        if (typeof this.isPinSelectable === 'function' && Array.isArray(countryCodes)) {
            return this.isPinSelectable(countryCodes, pinElement.dataset.categoryNames || '');
        }
        return !pinElement.classList.contains(DISABLED_PIN_CLASS);
    },

    renderGroupPins() {
        const groupLayer = this.$root.querySelector(`.${GROUP_LAYER_CLASS}`);
        if (!groupLayer) {
            return;
        }
        const labelTemplate = groupLayer.dataset.clusterLabel || '{0}';

        groupLayer.innerHTML = this.groupPins
            .map((groupPin, index) => {
                const count = groupPin.pins.length;
                const isDisabled = !groupPin.pins.some((pin) => this.isClusterPinSelectable(pin.element));
                const transform = `translate(${groupPin.x},${groupPin.y})`;
                // a disabled cluster is not operable, so it stays out of the tab order
                const interaction = isDisabled
                    ? 'aria-hidden="true"'
                    : `role="button" tabindex="0" aria-label="${labelTemplate.replace('{0}', count)}"`;
                return `
                    <g
                        class="${PIN_CLASS} ${GROUP_PIN_CLASS}${isDisabled ? ` ${DISABLED_PIN_CLASS}` : ''}"
                        ${interaction}
                        transform="${transform}"
                        data-transform="${transform}"
                        data-pin-size='{"width": ${PIN_SIZE.width}, "height": ${PIN_SIZE.height}}'
                        data-index="${index}"
                        x-bind:transform="pinTranslatePosition($el)">
                        <ellipse style="stroke-width:0;" cx="14.7786" cy="12.3268" rx="12.1653" ry="10.745"/>
                        <path
                            fill="currentColor"
                            stroke="white"
                            stroke-width="2"
                            d="m12.0521.3013C4.8702,1.8855.4343,9.173,2.3354,16.2493c.5281,1.5842,1.1618,2.9572,1.9011,4.4359,1.9011,3.4853,4.119,6.7594,6.337,10.0335.7393,1.0562,1.4786,2.1123,2.3236,3.1685.5281.6337,1.0562,1.373,1.5842,2.0067.2112.2112.5281.1056.7393-.1056.3168-.4225.6337-.8449.9505-1.2674.2112-.3168.7393-.9505.9505-1.2674,2.746-3.6966,5.1752-7.3931,7.4987-11.3009.9505-1.5842,1.7955-3.2741,2.4292-5.0696.1056-.4225.3168-.8449.4225-1.2674C29.4787,6.6383,21.135-1.7054,12.0521.3013m2.8701,19.971c-4.2417,0-7.5956-3.4525-7.5956-7.5956,0-4.2417,3.4525-7.5956,7.5956-7.5956s7.5956,3.4525,7.5956,7.5956c.0986,4.143-3.3539,7.5956-7.5956,7.5956"
                        ></path>
                        <text style="font-family: 'Avenir', sans-serif; font-size:15px; font-weight:900;isolation:isolate;" transform="translate(10 18)" fill="currentColor"><tspan x="0" y="0">${count}</tspan></text>
                    </g>
                `;
            })
            .join('');

        // Alpine initialises the injected pins asynchronously; nudging the zoom
        // makes their x-bind:transform run once they are known, so they are never
        // painted at the unscaled transform from the markup above. The value is
        // restored from the map rather than from a captured one, which would
        // clobber a zoom that changed while the nudge was pending.
        const zoom = this.zoom;
        this.zoom = zoom + 0.0001;
        clearTimeout(this.zoomNudgeTimer);
        this.zoomNudgeTimer = setTimeout(() => {
            this.zoom = this.map ? this.map.getZoom() : zoom;
        }, 1);
    },

    zoomIntoGroup(groupPinElement) {
        const group = this.groupPins[groupPinElement.dataset.index];
        if (!group || !group.pins.length) {
            return;
        }

        let minDistance = Infinity;
        group.pins.forEach((pinA, i) => {
            for (let j = i + 1; j < group.pins.length; j++) {
                minDistance = Math.min(minDistance, this.distanceBetween(pinA, group.pins[j]));
            }
        });

        // zoom just past the point where the two closest pins stop overlapping;
        // svg-pan-zoom clamps this to maxZoom, which is why very close pins stay
        // clustered (see the limitation noted at the top of this file)
        const mobileFactor = isMobileViewport() ? MOBILE_FACTOR : 1;
        const requiredZoom =
            minDistance !== Infinity
                ? Math.max(
                      MIN_SEPARATION_ZOOM,
                      (CLUSTER_RADIUS / minDistance) * SEPARATION_MARGIN * mobileFactor
                  )
                : this.map.getZoom() * 1.5;

        this.$root.classList.add(ANIMATED_CLASS);

        // pinTranslatePosition compensates the pin's scale around its tip, so the
        // tip is the one point that keeps its screen position at any zoom level —
        // centring on anything else drifts further the deeper the zoom goes.
        this.focusOn(
            {
                x: group.x + PIN_SIZE.width / 2,
                y: group.y + PIN_SIZE.height,
                width: 0,
                height: 0,
            },
            requiredZoom
        );

        this.zoom = this.map.getZoom();

        // the recompute replaces the whole group layer, so a keyboard user would
        // lose focus to <body>; hand it to whichever cluster now sits here
        const hadFocus =
            groupPinElement === document.activeElement || groupPinElement.contains(document.activeElement);
        this.recalculatePinClusters();
        if (hadFocus) {
            // still clustered → the cluster that took its place; separated → the
            // first member, which on the ReferencesMap is the reference link itself
            const next =
                this.$root.querySelector(`.${GROUP_PIN_CLASS}[data-index="${groupPinElement.dataset.index}"]`) ||
                (group.pins[0] && group.pins[0].element.closest('a'));
            if (next) {
                next.focus();
            }
        }

        clearTimeout(this.animationTimer);
        this.animationTimer = setTimeout(() => {
            this.$root.classList.remove(ANIMATED_CLASS);
        }, 300);
    },

    // taken from https://github.com/bumbu/svg-pan-zoom/issues/381 — zooming alone
    // does not keep the element in view, so pan it to the centre first
    focusOn(bbox, zoom) {
        const { width, height, realZoom } = this.map.getSizes();
        this.map.pan({
            x: -realZoom * (bbox.x - width / (realZoom * 2) + bbox.width / 2),
            y: -realZoom * (bbox.y - height / (realZoom * 2) + bbox.height / 2),
        });
        this.map.zoom(zoom);
    },
});
